// "Ask friends to pay" (lock-in inside, once called "Send totals"): the host's button on the board. Before it, friends tap items but don't see their
// total or a pay button. Free when nothing is owed (a free split, a credit, no fee for this table).
// Otherwise Stripe's own payment form opens in a sheet: Apple Pay or Google Pay first where the phone
// has them, card as the fallback. A card number never reaches us, and the split locks only once the
// server has checked with Stripe that it's paid.
import { compute, fmt } from '../../money.ts';
import { ASK_FRIENDS_TO_PAY, feeNote, PAY_SHEET_SUB, payBackLine, paySheetTitle, WAIVER_ON_OTHER } from '../../fee-copy.ts';
import type { PublicSplit } from '../../types.ts';
import { ApiError, lockSplit, type LockReply } from '../api.ts';
import { fee } from '../fee.ts';
import { closeSheet, esc, openSheet } from '../dom.ts';
import { PAY_ELEMENT_OPTIONS, payElementsOptions, STRIPE_JS_URL } from '../stripe-sheet.ts';

export const PAY_FAILED = "Payment didn't go through. Try again.";

/** Missing means a split from before lock-in, which counts as locked. */
export const isLocked = (s: PublicSplit): boolean => s.locked !== false;

/** A friend who tapped "Tell <Host> you're ready" (the host's view of the split only). */
export const saidReady = (s: PublicSplit, pid: string): boolean => (s.nudges || []).some((n) => n.pid === pid);

/**
 * Who has picked: tapped at least one item, or had nothing and said they're ready (a ready friend
 * with no items is done picking). When split evenly, everyone who joined.
 */
function pickedCount(s: PublicSplit): number {
  if (s.even) return s.people.length;
  return s.people.filter((p) => saidReady(s, p.id) || Object.values(s.claims).some((list) => list.some((c) => c.pid === p.id))).length;
}

/** "2 of 3 picked": people who picked (see pickedCount), out of the table. */
export function pickedLine(s: PublicSplit): string {
  const table = Math.max(s.partySize || 0, s.people.length);
  return `${pickedCount(s)} of ${table} ${s.even ? 'joined' : 'picked'}`;
}

/**
 * The one line under the host's button: who asked to see their totals, then who has picked.
 * "Jen is ready · 1 of 2 picked", "2 friends are ready · 2 of 3 picked", or just "1 of 2 picked".
 */
export function readyLine(s: PublicSplit): string {
  const asked = s.nudges || [];
  const who = asked.length === 1 ? `${asked[0].name} is ready · ` : asked.length > 1 ? `${asked.length} friends are ready · ` : '';
  return who + pickedLine(s);
}

/** Everyone at the table has picked (or joined, when split evenly): "3 of 3 picked". */
export function allPicked(s: PublicSplit): boolean {
  const table = Math.max(s.partySize || 0, s.people.length);
  return table > 0 && pickedCount(s) >= table;
}

/**
 * After sending, what nobody claimed: "Soup isn't claimed. It's on you unless someone taps it." or
 * "Soup and Fries aren't claimed. They're on you unless someone taps them."
 */
export function unclaimedSentLine(names: string[]): string {
  if (!names.length) return '';
  const list = names.length === 1 ? names[0] : names.slice(0, -1).join(', ') + ' and ' + names[names.length - 1];
  return names.length === 1 ? `${list} isn't claimed. It's on you unless someone taps it.` : `${list} aren't claimed. They're on you unless someone taps them.`;
}

/** After sending: "0 of 2 paid", friends only. A friend with nothing to pay counts as paid. */
export function paidLine(s: PublicSplit): string {
  const P = compute(s).payer;
  return `${P.paidCount} of ${P.friendCount} paid`;
}

/** What the host's one green button does right now, on the board. */
export type HostStep = 'pick' | 'send' | 'done';

/**
 * The note under "Ask friends to pay": at most 2 short lines, plus the picked count while people are
 * still picking (host-button copy PRD, Oct 21, 2026).
 *   still picking:  "1 of 3 picked" · "This split is free." / "This split is $1.49."
 *   everyone in:    no fee: "This split is free." · "Your friends pay you back $X."
 *                   paid:   "Your friends pay you back $X. You pay the $1.49 app fee." (A), or
 *                           "Your N friends pay you back $X. That covers the $1.49, so it costs you $0." (C)
 * Plus "Your free split is your other open one." on a paid split when the host's older open split
 * is the one with no fee. $X is the yellow banner's number; at $0.00 the pay-back line is skipped.
 * While pricing is off (PRICING_ENABLED) there's no fee line at all: "1 of 3 picked" while people
 * pick, then nothing once everyone has picked. The pay-back and "free" lines stay for when pricing is on.
 */
export function hostNote(s: PublicSplit, pricing = fee.pricing): Array<{ id: string; text: string }> {
  const friends = s.people.filter((p) => !p.isPayer).length;
  if (!pricing) {
    return allPicked(s) ? [] : [{ id: 'lockSub', text: readyLine(s) }];
  }
  const cents = (s.lock || { cents: 0 }).cents;
  const paid = cents > 0;
  const waiver = paid && s.waiverOnOther ? [{ id: 'waiverOther', text: WAIVER_ON_OTHER }] : [];
  if (!allPicked(s)) return [{ id: 'lockSub', text: readyLine(s) }, { id: 'feeNote', text: feeNote(cents) }, ...waiver];
  const back = payBackLine(s.fee?.group, friends, compute(s).payer.comingBack, cents);
  const lines = paid
    ? [...(back ? [{ id: 'payBack', text: back }] : [{ id: 'feeNote', text: feeNote(cents) }]), ...waiver]
    : [{ id: 'feeNote', text: feeNote(cents) }, ...(back ? [{ id: 'payBack', text: back }] : [])];
  return lines;
}

/**
 * The host's sticky foot: one green button and one line under it. First "Pick my items" (with a
 * small way past it for a host who had nothing), then "Ask friends to pay", then "Done".
 */
export function hostFoot(s: PublicSplit, step: HostStep): string {
  if (step === 'done') {
    return `<button class="btn" data-act="close" id="doneBtn">Done</button>
    <p class="hint" id="lockSub" style="text-align:center;margin:0">${esc(paidLine(s))}</p>`;
  }
  if (step === 'pick') {
    return `<div class="error" id="lockError" role="alert" hidden></div>
    <button class="btn" data-act="mine" id="pickBtn">Pick my items</button>
    <p class="hint" id="lockSub" style="text-align:center;margin:0">${esc(readyLine(s))}</p>
    <button class="link" data-act="lock" id="skipPick" style="align-self:center;padding:0;font-size:13px">Had nothing? ${esc(ASK_FRIENDS_TO_PAY)}</button>
    ${fee.pricing ? `<p class="hint" id="skipFee" style="text-align:center;margin:-6px 0 0">${esc(feeNote((s.lock || { cents: 0 }).cents))}</p>` : ''}`;
  }
  return `<div class="error" id="lockError" role="alert" hidden></div>${lockFoot(s)}`;
}

/** The "Ask friends to pay" button and the note under it. */
export function lockFoot(s: PublicSplit): string {
  return `<button class="btn" data-act="lock" id="lockBtn">${esc(ASK_FRIENDS_TO_PAY)}</button>
    ${hostNote(s).map((l) => `<p class="hint" id="${l.id}" style="text-align:center;margin:0">${esc(l.text)}</p>`).join('\n    ')}`;
}

// Stripe.js has no types here; only these few calls are used.
/* eslint-disable @typescript-eslint/no-explicit-any */
let stripeLoad: Promise<any> | null = null;
/** Stripe asks that Stripe.js always load from js.stripe.com, never from a copy. */
function loadStripe(key: string): Promise<any> {
  stripeLoad ??= new Promise((resolve, reject) => {
    const el = document.createElement('script');
    el.src = STRIPE_JS_URL;
    el.onload = () => resolve((window as any).Stripe(key));
    el.onerror = () => { stripeLoad = null; reject(new Error("The payment form didn't load. Check your connection and try again.")); };
    document.head.appendChild(el);
  });
  return stripeLoad;
}

/** Tapped "Ask friends to pay". Resolves true once the split is locked. */
export async function lockIn(code: string, key: string, btn: HTMLButtonElement | null): Promise<boolean> {
  const label = btn?.textContent || '';
  if (btn) { btn.disabled = true; btn.textContent = 'Sending…'; }
  try {
    const r = await lockSplit(code, key);
    if (r.locked) return true;
    if (r.processing) throw new Error('Your payment is still going through. This updates when it does.');
    if (r.pay) return await paySheet(code, key, r.pay);
    return false;
  } catch (err) {
    showBoardError((err as Error).message);
    return false;
  } finally {
    if (btn && btn.isConnected) { btn.disabled = false; btn.textContent = label; }
  }
}

function showBoardError(msg: string): void {
  const el = document.getElementById('lockError');
  if (el) { el.textContent = msg; el.hidden = !msg; }
}

/** Stripe's payment form in a sheet. Resolves true when paid and locked, false when closed. */
function paySheet(code: string, key: string, pay: NonNullable<LockReply['pay']>): Promise<boolean> {
  return new Promise((resolve) => {
    let elements: any = null;
    let stripe: any = null;
    let busy = false;
    const box = openSheet(`
      <div class="stack" style="gap:6px">
        <h2 class="h" style="font-size:22px">${esc(paySheetTitle(pay.cents))}</h2>
        <p class="sub" id="lockPaySub" style="margin:0">${esc(PAY_SHEET_SUB)}</p>
      </div>
      <div id="lockPayForm" style="min-height:120px"><div class="sub">Loading…</div></div>
      <div class="error" id="lockPayError" role="alert" hidden></div>
      <button class="btn" data-act="lockPay" id="lockPayBtn" disabled>Pay ${fmt(pay.cents)}</button>
      <p class="hint" style="text-align:center;margin:0">Paid through Stripe. We never see your card number.</p>`,
    async (e) => {
      const b = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-act="lockPay"]');
      if (!b || !elements || busy) return;
      busy = true;
      b.disabled = true;
      b.textContent = 'Paying…';
      setError('');
      try {
        // Deferred Elements: check the form first, then confirm the card-only payment the server made.
        const checked = await elements.submit();
        if (checked?.error) throw new Error(checked.error.message || PAY_FAILED);
        const { error, paymentIntent } = await stripe.confirmPayment({ elements, clientSecret: pay.clientSecret, confirmParams: { return_url: location.href }, redirect: 'if_required' });
        if (error || !paymentIntent) throw new Error(PAY_FAILED);
        const r = await lockSplit(code, key, paymentIntent.id);
        if (r.processing) throw new Error('Your payment is still going through. This updates when it does.');
        if (!r.locked) throw new Error(PAY_FAILED);
        closeSheet();
        resolve(true);
        return;
      } catch (err) {
        setError(err instanceof ApiError && err.status === 402 ? PAY_FAILED : (err as Error).message || PAY_FAILED);
      }
      busy = false;
      b.disabled = false;
      b.textContent = `Pay ${fmt(pay.cents)}`;
    });
    const setError = (msg: string) => { const el = box.querySelector<HTMLElement>('#lockPayError'); if (el) { el.textContent = msg; el.hidden = !msg; } };
    // Closing the sheet without paying leaves the split unlocked.
    const watch = new MutationObserver(() => { if (!box.isConnected) { watch.disconnect(); resolve(false); } });
    watch.observe(document.body, { childList: true, subtree: true });
    loadStripe(pay.publishableKey)
      .then((s) => {
        stripe = s;
        const css = getComputedStyle(document.documentElement);
        elements = stripe.elements(payElementsOptions(pay.cents, { theme: 'stripe', variables: { colorPrimary: css.getPropertyValue('--accent').trim() || undefined, borderRadius: '12px' } }));
        const el = box.querySelector<HTMLElement>('#lockPayForm');
        if (!el) return;
        el.innerHTML = '';
        // The payment is card only; Apple Pay and Google Pay stay on as wallets for that card.
        elements.create('payment', PAY_ELEMENT_OPTIONS).mount(el);
        const b = box.querySelector<HTMLButtonElement>('#lockPayBtn');
        if (b) b.disabled = false;
      })
      .catch((err: Error) => setError(err.message));
  });
}
